import React, { useEffect, useState, useMemo } from "react";
import { FaEye } from "react-icons/fa";
import { toast } from "react-toastify";
import { getAllASectionResults } from "../../services/asectionResultService";
import { getMatches } from "../../services/matchService";
import { getLeagues } from "../../services/leagueService";

export const AsectionsQuestionsListResultLayer = () => {
  const [results, setResults] = useState([]);
  const [matches, setMatches] = useState([]);
  const [leagues, setLeagues] = useState([]);

  const [viewData, setViewData] = useState(null);
  const [loading, setLoading] = useState(false);

  /* FILTER STATES */
  const [leagueId, setLeagueId] = useState("");
  const [matchId, setMatchId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  /* PAGINATION */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [resultRes, matchRes, leagueRes] = await Promise.all([
        getAllASectionResults(),
        getMatches(),
        getLeagues(),
      ]);

      if (resultRes?.status)
        setResults(resultRes.results || []);

      if (matchRes?.status)
        setMatches(matchRes.data || []);

      if (leagueRes?.status)
        setLeagues(leagueRes.data || []);
    } catch {
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  /* ================= MATCH FILTER BY LEAGUE ================= */
  const filteredMatches = useMemo(() => {
    if (!leagueId) return matches;
    return matches.filter(
      (m) => String(m.league_id) === String(leagueId)
    );
  }, [leagueId, matches]);

  /* ================= MAIN FILTER ================= */
  const filteredResults = useMemo(() => {
    return results.filter((r) => {
      const matchObj = matches.find(
        (m) =>
          m.team_a === r?.question_details?.team_a &&
          m.team_b === r?.question_details?.team_b
      );

      const leagueMatch = leagueId
        ? matchObj &&
          String(matchObj.league_id) === String(leagueId)
        : true;

      const matchFilter = matchId
        ? matchObj &&
          String(matchObj.id) === String(matchId)
        : true;

      const statusMatch = statusFilter
        ? r?.question_details?.status === statusFilter
        : true;

      const searchMatch =
        r?.question_details?.question
          ?.toLowerCase()
          .includes(search.toLowerCase());

      return (
        leagueMatch &&
        matchFilter &&
        statusMatch &&
        searchMatch
      );
    });
  }, [results, leagueId, matchId, statusFilter, search, matches]);

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(filteredResults.length / pageSize);

  const paginatedData = filteredResults.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  /* ================= VIEW ================= */
  const handleView = (row) => {
    setViewData(row);
  };

  return (
    <div className="container-fluid mt-4">
      <div className="card shadow-sm border-0">

        <div className="card-header bg-white">
          <h4 className="fw-bold mb-0">
            Section A Results
          </h4>
        </div>

        <div className="card-body">

          {/* FILTER BAR */}
          <div className="row g-3 mb-3">

            <div className="col-12 col-md-2">
              <label className="form-label">League</label>
              <select
                className="form-select"
                value={leagueId}
                onChange={(e) => {
                  setLeagueId(e.target.value);
                  setMatchId("");
                  setPage(1);
                }}
              >
                <option value="">All</option>
                {leagues.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.league_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-2">
              <label className="form-label">Match</label>
              <select
                className="form-select"
                value={matchId}
                onChange={(e) => {
                  setMatchId(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All</option>
                {filteredMatches.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.team_a} vs {m.team_b}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All</option>
                <option value="settled">Settled</option>
                <option value="active">Active</option>
              </select>
            </div>

            <div className="col-12 col-md-2">
              <label className="form-label">Show</label>
              <select
                className="form-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label">Search</label>
              <input
                className="form-control"
                placeholder="Search question"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>

          </div>

          {/* TABLE */}
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Match</th>
                  <th>Question</th>
                  <th>Winner</th>
                  <th>Status</th>
                  <th>View</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      Loading...
                    </td>
                  </tr>
                ) : paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No results found
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((r, i) => (
                    <tr key={i}>
                      <td>
                        {(page - 1) * pageSize + i + 1}
                      </td>
                      <td>
                        {r?.question_details?.team_a} vs{" "}
                        {r?.question_details?.team_b}
                      </td>
                      <td>
                        {r?.question_details?.question}
                      </td>
                      <td>
                        {
                          r?.question_details
                            ?.winning_option
                        }
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            r?.question_details
                              ?.status === "settled"
                              ? "bg-success"
                              : "bg-warning text-dark"
                          }`}
                        >
                          {
                            r?.question_details
                              ?.status
                          }
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => handleView(r)}
                        >
                          <FaEye />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-end mt-3">
              <ul className="pagination mb-0">
                {[...Array(totalPages)].map((_, i) => (
                  <li
                    key={i}
                    className={`page-item ${
                      page === i + 1 ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

        </div>
      </div>

      {/* MODAL SAME AS BEFORE */}
      {viewData && (
        <>
          <div
            className="modal fade show d-block"
            style={{ background: "rgba(0,0,0,0.6)" }}
          >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="fw-bold">
                    {
                      viewData?.question_details
                        ?.question
                    }
                  </h5>
                  <button
                    className="btn-close"
                    onClick={() => setViewData(null)}
                  />
                </div>
                <div className="modal-body">
                  {/* KEEP YOUR EXISTING MODAL BODY */}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};